import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { BRAND } from '@guanwei/shared';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/lib/auth.store';

export function HomePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const scrollToFeatures = () => {
    const el = document.getElementById('features');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="flex-1">
      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <GoldenFlowBackground />
        <div className="relative z-10 mx-auto max-w-6xl px-4 pb-20 pt-16 text-center sm:px-6 sm:pb-28 sm:pt-20 lg:pb-32 lg:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0, 0, 1] }}
          >
            <p className="mb-5 text-xs uppercase tracking-[0.32em] text-fg-muted sm:mb-6 sm:text-sm sm:tracking-[0.4em]">
              {BRAND.nameEn}
            </p>
            <h1 className="font-serif text-4xl leading-tight text-fg sm:text-5xl md:text-7xl">
              观天地之微
              <br />
              <span className="text-accent">察人事之要</span>
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-fg-1 sm:mt-8 sm:text-lg">
              不算命，算战略。
              <br />
              基于八字与易理的 AI 决策伴侣。
            </p>

            <div className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:mt-12 sm:flex-row sm:items-center sm:gap-4">
              <Button size="lg" onClick={() => navigate('/chart')}>
                立即排盘
              </Button>
              <Button size="lg" variant="outline" onClick={scrollToFeatures}>
                了解更多
              </Button>
            </div>

            <p className="mt-6 text-xs text-fg-muted">
              免费体验排盘与简版报告 · 完整 7 段式深度报告需解锁
            </p>
          </motion.div>
        </div>
      </section>

      {/* 核心能力 */}
      <section id="features" className="border-t border-border bg-bg-1">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <h2 className="font-serif text-3xl text-fg text-center mb-3">核心能力</h2>
          <p className="mb-10 text-center text-sm text-fg-2 sm:mb-12">
            从一份命盘出发，覆盖人生关键决策场景
          </p>
          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <motion.button
                key={f.title}
                onClick={() => navigate(f.to)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group rounded-lg border border-border bg-bg p-5 text-left transition-all duration-base ease-standard hover:border-border-accent hover:shadow-gold sm:p-8"
              >
                <div className="font-serif text-3xl text-accent mb-4">{f.symbol}</div>
                <h3 className="font-serif text-xl text-fg mb-2 group-hover:text-accent transition-colors">
                  {f.title}
                </h3>
                <p className="text-sm text-fg-2 leading-relaxed mb-4">{f.desc}</p>
                <span className="text-xs text-accent tracking-wider">{f.cta} →</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* 为什么是观微 */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <h2 className="font-serif text-3xl text-fg text-center mb-3">为什么是观微</h2>
          <p className="mb-10 text-center text-sm text-fg-2 sm:mb-12">把传统命理放进现代决策框架</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div
                key={h.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-lg border border-border bg-bg-1 p-5 sm:p-8"
              >
                <div className="text-xs tracking-[0.3em] text-accent mb-3 uppercase">{h.label}</div>
                <h3 className="font-serif text-xl text-fg mb-3">{h.title}</h3>
                <p className="text-sm text-fg-2 leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-bg-1">
        <div className="mx-auto max-w-3xl px-4 py-14 text-center sm:px-6 sm:py-20">
          <h2 className="mb-5 font-serif text-2xl text-fg sm:text-3xl md:text-4xl">
            {user ? '继续你的下一次推演' : '从一份免费命盘开始'}
          </h2>
          <p className="mb-8 leading-relaxed text-fg-2 sm:mb-10">
            {user
              ? `欢迎回来，${user.name ?? user.email}。重要决策，多一个视角。`
              : '邮箱注册即可体验排盘与简版报告，完整深度分析按需解锁。'}
          </p>
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center sm:gap-4">
            {user ? (
              <>
                <Button size="lg" onClick={() => navigate('/chart')}>
                  开始排盘
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/chat')}>
                  追问对话
                </Button>
              </>
            ) : (
              <>
                <Link to="/auth/register">
                  <Button size="lg">免费注册</Button>
                </Link>
                <Link to="/auth/login">
                  <Button size="lg" variant="outline">
                    已有账号 · 登录
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function GoldenFlowBackground() {
  return (
    <div aria-hidden="true" className="home-golden-flow pointer-events-none absolute inset-0 -z-10">
      <div className="home-golden-flow__glow home-golden-flow__glow--left" />
      <div className="home-golden-flow__glow home-golden-flow__glow--right" />
      <svg
        className="home-golden-flow__svg home-golden-flow__svg--slow"
        viewBox="0 0 1440 720"
        preserveAspectRatio="none"
      >
        <path d="M-80 474 C 190 270, 374 664, 650 420 S 1110 126, 1520 288" />
        <path d="M-120 250 C 158 148, 320 348, 558 290 S 990 44, 1500 118" />
        <path d="M-90 606 C 250 510, 458 716, 750 548 S 1168 312, 1510 448" />
      </svg>
      <svg
        className="home-golden-flow__svg home-golden-flow__svg--fast"
        viewBox="0 0 1440 720"
        preserveAspectRatio="none"
      >
        <path d="M-120 360 C 180 520, 412 150, 690 332 S 1118 596, 1540 230" />
        <path d="M-80 144 C 240 222, 390 72, 682 164 S 1080 346, 1500 196" />
      </svg>
      <div className="home-golden-flow__veil" />
    </div>
  );
}

const FEATURES = [
  {
    symbol: '盤',
    title: '八字精准排盘',
    desc: '真太阳时校正、流派定盘对照，让命盘不再有歧义。',
    to: '/chart',
    cta: '立即排盘',
  },
  {
    symbol: '覽',
    title: '七段式深度报告',
    desc: '格局定调、天赋性格、财富事业、感情、健康、流年、行动清单。',
    to: '/chart',
    cta: '查看报告',
  },
  {
    symbol: '問',
    title: '沉浸式追问对话',
    desc: '基于你的命盘上下文，长期陪伴你的人生决策。',
    to: '/chat',
    cta: '开始对话',
  },
  {
    symbol: '占',
    title: '六爻起卦决策',
    desc: '心静过滤、互动起卦、当下未来对照，重要决策的第二视角。',
    to: '/divination',
    cta: '即刻起卦',
  },
  {
    symbol: '合',
    title: '关系合盘',
    desc: '双方命盘对照，关系动力学分析，关键冲突点与相处策略。',
    to: '/compatibility',
    cta: '合盘分析',
  },
  {
    symbol: '運',
    title: '流年与大运规划',
    desc: '当前流年月度颗粒度，叠加 10 年大运长周期，行动有的放矢。',
    to: '/fortune',
    cta: '查看流年',
  },
];

const HIGHLIGHTS = [
  {
    label: 'METHOD',
    title: '定盘机制',
    desc: '不武断给一个答案。先用 LLM 生成流派对照卡，由你视觉化选择，再锁定流派出报告。',
  },
  {
    label: 'STRUCTURE',
    title: '结构化输出',
    desc: '七段式固定结构，不写玄而又玄的辞藻；每一段都给出可执行的判断与行动建议。',
  },
  {
    label: 'PRIVACY',
    title: '严肃合规',
    desc: '出生信息字段级加密，可一键删除全部数据；不做生死、疾病、犯罪等敏感预测。',
  },
];
